import { formatCurrency } from './formatters';
import { calculateFormatOutput } from './scenarioCalculations';
import { FORMAT_DEFINITIONS } from './constants';

/* ── SVG icon helpers ── */
const IconFlask = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 3h6M9 3v7l-5 9a1 1 0 0 0 .9 1.5h14.2A1 1 0 0 0 20 19l-5-9V3" />
        <line x1="6.5" y1="14.5" x2="17.5" y2="14.5" />
    </svg>
);

const IconDroplet = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C6.5 9 4 13.5 4 16a8 8 0 0 0 16 0c0-2.5-2.5-7-8-14z" />
    </svg>
);

const IconBox = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="21 8 21 21 3 21 3 8" />
        <rect x="1" y="3" width="22" height="5" />
        <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
);

const IconTrendUp = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
    </svg>
);

const IconActivity = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);

const IconArrowRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

/* ── Component ── */
const GuidedStickyHeader = ({
    activeProduct,
    availableKegs,
    selectedFormatId,
    quantityValue,
    viewMode,
    canCreatePlan,
    blockedReason,
    onCreatePlan,
}) => {
    const format = FORMAT_DEFINITIONS[selectedFormatId];

    let estOutput = '—';
    let wsRevenue = 0;
    let retailRevenue = 0;

    if (format && quantityValue > 0) {
        if (viewMode === 'target') {
            estOutput = `${Math.floor(quantityValue).toLocaleString()} ${format.unit}`;
            wsRevenue = quantityValue * format.pricing.ws;
            retailRevenue = quantityValue * format.pricing.retail;
        } else {
            const result = calculateFormatOutput(quantityValue, selectedFormatId);
            estOutput = `${Math.floor(result.units).toLocaleString()} ${format.unit}`;
            wsRevenue = result.wsRevenue;
            retailRevenue = result.retailRevenue;
        }
    }

    const concentrateDisplay = availableKegs > 0
        ? `${availableKegs.toLocaleString(undefined, { maximumFractionDigits: 2 })} kegs`
        : '—';

    const revenueDisplay = wsRevenue > 0
        ? `${formatCurrency(wsRevenue)} / ${formatCurrency(retailRevenue)}`
        : '—';

    const statusLabel = canCreatePlan ? 'Ready' : quantityValue > 0 ? 'In Progress' : 'Not Started';
    const statusColor = canCreatePlan ? 'ready' : quantityValue > 0 ? 'progress' : 'idle';

    return (
        <div className="gsp-sticky-header">
            {/* Ambient decorative glows */}
            <div className="gsp-hero-glow gsp-hero-glow-l" aria-hidden="true" />
            <div className="gsp-hero-glow gsp-hero-glow-r" aria-hidden="true" />

            {/* ─── LEFT — Product identity (≈25%) ─── */}
            <div className="gsp-hero-product">
                <div className="gsp-hero-product-icon">
                    <IconFlask />
                </div>
                <div className="gsp-hero-product-meta">
                    <span className="gsp-hero-eyebrow">Scenario Planning</span>
                    <h1 className="gsp-hero-product-name">
                        {activeProduct?.product_name || 'Select a Product'}
                    </h1>
                    {activeProduct && (
                        <span className="gsp-active-badge">Active</span>
                    )}
                </div>
            </div>

            {/* ─── CENTER — 4 KPI cards ─── */}
            <div className="gsp-hero-kpis">

                <div className="gsp-hero-kpi-card">
                    <div className="gsp-hero-kpi-icon">
                        <IconDroplet />
                    </div>
                    <div className="gsp-hero-kpi-body">
                        <span className="gsp-hero-kpi-label">Concentrate Available</span>
                        <strong className="gsp-hero-kpi-value">{concentrateDisplay}</strong>
                    </div>
                </div>

                <div className="gsp-hero-kpi-card">
                    <div className="gsp-hero-kpi-icon">
                        <IconBox />
                    </div>
                    <div className="gsp-hero-kpi-body">
                        <span className="gsp-hero-kpi-label">Estimated Output</span>
                        <strong className="gsp-hero-kpi-value">{estOutput}</strong>
                    </div>
                </div>

                <div className="gsp-hero-kpi-card">
                    <div className="gsp-hero-kpi-icon">
                        <IconTrendUp />
                    </div>
                    <div className="gsp-hero-kpi-body">
                        <span className="gsp-hero-kpi-label">Estimated Revenue</span>
                        <strong className="gsp-hero-kpi-value">{revenueDisplay}</strong>
                    </div>
                </div>

                <div className="gsp-hero-kpi-card">
                    <div className="gsp-hero-kpi-icon">
                        <IconActivity />
                    </div>
                    <div className="gsp-hero-kpi-body">
                        <span className="gsp-hero-kpi-label">Production Status</span>
                        <strong className={`gsp-hero-kpi-value gsp-status-${statusColor}`}>
                            {statusLabel}
                        </strong>
                    </div>
                </div>
            </div>

            {/* ─── RIGHT — Primary action ─── */}
            <div className="gsp-hero-actions">
                <button
                    className={`gsp-hero-create-btn ${canCreatePlan ? 'enabled' : ''}`}
                    disabled={!canCreatePlan}
                    onClick={onCreatePlan}
                    title={canCreatePlan ? 'Create production plan' : blockedReason}
                >
                    <span className="gsp-hero-btn-text">Create Production Plan</span>
                    <span className="gsp-hero-btn-icon"><IconArrowRight /></span>
                </button>
                {!canCreatePlan && blockedReason && (
                    <p className="gsp-hero-btn-hint">
                        {blockedReason}
                    </p>
                )}
            </div>
        </div>
    );
};

export default GuidedStickyHeader;
